import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useClearErrorLogsMutation, useGetErrorLogsQuery } from '../slices/errorLogsApiSlice';

const DEFAULT_SORT = { key: 'createdAt', direction: 'desc' };
const EMPTY_LOGS = [];

const formatTimestamp = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const getEnvironmentLabel = (log) => [log.browser, log.os, log.device].filter(Boolean).join(' / ');

const getLogSearchText = (log) => {
  try {
    return JSON.stringify(log).toLowerCase();
  } catch {
    return '';
  }
};

const compareValues = (left, right, direction) => {
  if (typeof left === 'number' || typeof right === 'number') {
    const leftNumber = Number.isFinite(left) ? left : Number.NEGATIVE_INFINITY;
    const rightNumber = Number.isFinite(right) ? right : Number.NEGATIVE_INFINITY;
    return direction === 'asc' ? leftNumber - rightNumber : rightNumber - leftNumber;
  }

  const leftValue = String(left || '');
  const rightValue = String(right || '');
  return direction === 'asc'
    ? leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: 'base' })
    : rightValue.localeCompare(leftValue, undefined, { numeric: true, sensitivity: 'base' });
};

const AdminErrorLogsScreen = () => {
  const [source, setSource] = useState('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
  const { data, isLoading, error, refetch, isFetching } = useGetErrorLogsQuery({ source, limit: 150 });
  const [clearErrorLogs, { isLoading: isClearing }] = useClearErrorLogsMutation();

  const logs = data?.logs || EMPTY_LOGS;
  const filteredLogs = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) {
      return logs;
    }

    return logs.filter((log) => getLogSearchText(log).includes(keyword));
  }, [logs, query]);

  const sortedLogs = useMemo(() => {
    const nextLogs = [...filteredLogs];
    const { key, direction } = sortConfig;

    nextLogs.sort((left, right) => {
      switch (key) {
        case 'source':
          return compareValues(left.source, right.source, direction);
        case 'statusCode':
          return compareValues(left.statusCode, right.statusCode, direction);
        case 'message':
          return compareValues(left.message, right.message, direction);
        case 'environment':
          return compareValues(getEnvironmentLabel(left), getEnvironmentLabel(right), direction);
        case 'createdAt':
        default:
          return compareValues(new Date(left.createdAt).getTime(), new Date(right.createdAt).getTime(), direction);
      }
    });

    return nextLogs;
  }, [filteredLogs, sortConfig]);

  const selectedLog = useMemo(
    () => sortedLogs.find((log) => log._id === selectedId) || sortedLogs[0] || null,
    [sortedLogs, selectedId]
  );

  const handleSort = (key) => {
    setSortConfig((currentSort) => (
      currentSort.key === key
        ? { key, direction: currentSort.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'createdAt' ? 'desc' : 'asc' }
    ));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return '';
    }

    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const handleClear = async () => {
    if (!window.confirm('Clear all stored error logs?')) return;
    try {
      const result = await clearErrorLogs().unwrap();
      toast.success(`Deleted ${result.deletedCount} error logs.`);
      setSelectedId(null);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to clear error logs');
    }
  };

  return (
    <div className="section-modern">
      <div className="container-modern">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h1 className="text-primary mb-1">Error Logs</h1>
            <p className="text-secondary mb-0">Inspect client crashes and backend failures with request and device context.</p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => refetch()} disabled={isFetching}>Refresh</Button>
            <Button variant="outline-danger" onClick={handleClear} disabled={isClearing}>Clear Logs</Button>
          </div>
        </div>

        <Card className="modern-card mb-4">
          <Card.Body>
            <Row className="g-3">
              <Col md={3}>
                <Form.Select value={source} onChange={(e) => setSource(e.target.value)}>
                  <option value="">All sources</option>
                  <option value="client">Client</option>
                  <option value="server">Server</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Control
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter loaded logs by any field, including user, browser, stack, or metadata"
                />
              </Col>
              <Col md={3} className="d-flex align-items-center">
                <span className="text-secondary small">{sortedLogs.length} of {logs.length} logs shown</span>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error?.data?.message || 'Failed to load error logs'}</Alert>}

        <Row className="g-4">
          <Col lg={7}>
            <Card className="modern-card">
              <Card.Body>
                {isLoading ? (
                  <div className="text-center py-4"><Spinner animation="border" /></div>
                ) : sortedLogs.length === 0 ? (
                  <p className="text-secondary mb-0">{logs.length === 0 ? 'No error logs yet.' : 'No error logs match the current filters.'}</p>
                ) : (
                  <div className="table-responsive">
                    <Table hover>
                      <thead>
                        <tr>
                          <th><button type="button" className="btn btn-link p-0 text-decoration-none text-reset fw-semibold" onClick={() => handleSort('createdAt')}>Time{getSortIndicator('createdAt')}</button></th>
                          <th><button type="button" className="btn btn-link p-0 text-decoration-none text-reset fw-semibold" onClick={() => handleSort('source')}>Source{getSortIndicator('source')}</button></th>
                          <th><button type="button" className="btn btn-link p-0 text-decoration-none text-reset fw-semibold" onClick={() => handleSort('statusCode')}>Status{getSortIndicator('statusCode')}</button></th>
                          <th><button type="button" className="btn btn-link p-0 text-decoration-none text-reset fw-semibold" onClick={() => handleSort('message')}>Message{getSortIndicator('message')}</button></th>
                          <th><button type="button" className="btn btn-link p-0 text-decoration-none text-reset fw-semibold" onClick={() => handleSort('environment')}>Environment{getSortIndicator('environment')}</button></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedLogs.map((log) => (
                          <tr key={log._id} onClick={() => setSelectedId(log._id)} style={{ cursor: 'pointer' }}>
                            <td>{formatTimestamp(log.createdAt)}</td>
                            <td><Badge bg={log.source === 'server' ? 'danger' : 'primary'}>{log.source}</Badge></td>
                            <td>{log.statusCode || '-'}</td>
                            <td>{log.message}</td>
                            <td>{getEnvironmentLabel(log) || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={5}>
            <Card className="modern-card">
              <Card.Body>
                {!selectedLog ? (
                  <p className="text-secondary mb-0">Select an error to inspect details.</p>
                ) : (
                  <>
                    <div className="d-flex gap-2 align-items-center flex-wrap mb-3">
                      <Badge bg={selectedLog.source === 'server' ? 'danger' : 'primary'}>{selectedLog.source}</Badge>
                      {selectedLog.statusCode ? <Badge bg="secondary">{selectedLog.statusCode}</Badge> : null}
                      <span className="small text-secondary">{formatTimestamp(selectedLog.createdAt)}</span>
                    </div>
                    <h3 className="h5">{selectedLog.message}</h3>
                    <p className="small text-secondary mb-3">{selectedLog.path || selectedLog.route || selectedLog.url || 'Unknown path'}</p>
                    <div className="small mb-3">
                      <div><strong>User:</strong> {selectedLog.userEmail || selectedLog.user || 'Anonymous'}</div>
                      <div><strong>Browser:</strong> {[selectedLog.browser, selectedLog.os, selectedLog.device].filter(Boolean).join(' / ') || 'Unknown'}</div>
                      <div><strong>User Agent:</strong> {selectedLog.userAgent || 'Unknown'}</div>
                      <div><strong>Method:</strong> {selectedLog.method || '-'}</div>
                      <div><strong>Origin:</strong> {selectedLog.origin || '-'}</div>
                      <div><strong>Referer:</strong> {selectedLog.referer || '-'}</div>
                      <div><strong>IP:</strong> {selectedLog.ip || '-'}</div>
                    </div>
                    <div className="small">
                      <strong>Stack</strong>
                      <pre className="bg-light p-2 rounded small mt-2">{selectedLog.stack || 'No stack trace captured.'}</pre>
                    </div>
                    <div className="small mt-3">
                      <strong>Details</strong>
                      <pre className="bg-light p-2 rounded small mt-2">{JSON.stringify({
                        query: selectedLog.query,
                        bodyPreview: selectedLog.bodyPreview,
                        errorDetails: selectedLog.errorDetails,
                        metadata: selectedLog.metadata,
                        viewport: selectedLog.viewport,
                      }, null, 2)}</pre>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AdminErrorLogsScreen;
